import axios from 'axios';
import { apiKeyService } from './apiKeyService';

// Interfaces pour les résultats de l'API WhatCMS
interface WhatCmsResult {
  code: number;
  msg: string;
}

interface WhatCmsTechnology {
  name: string;
  id: number;
  version?: string;
  categories: string[];
  url: string;
}

interface WhatCmsSocialProfile {
  network: string;
  url: string;
  profile: string;
}

interface WhatCmsResponse {
  request: string;
  result: WhatCmsResult;
  last_checked: number;
  results: WhatCmsTechnology[];
  meta?: {
    social?: WhatCmsSocialProfile[];
  };
}

// Format de résultats adapté à notre application
export interface DetectionResult {
  detected: boolean;
  cms: string | null;
  confidence: number;
  version?: string;
  details?: any;
  logo?: string;
  technologies?: WhatCmsTechnology[];
  socialProfiles?: WhatCmsSocialProfile[];
}

// Logo URLs pour les CMS courants
const cmsLogos: Record<string, string> = {
  'WordPress': 'https://s.w.org/style/images/about/WordPress-logotype-standard.png',
  'Shopify': 'https://cdn.shopify.com/static/shopify-favicon.png',
  'Wix': 'https://www.wix.com/favicon.ico',
  'Webflow': 'https://assets-global.website-files.com/5d3e265ac89f6a3e64292efc/5d5595354de4fbfaafbfc0a9_webflow-favicon.svg',
  'Squarespace': 'https://static1.squarespace.com/static/ta/5134cbefe4b0c6fb04df8065/10515/assets/favicon.ico',
  'Joomla': 'https://www.joomla.org/templates/joomla/favicon.ico',
  'Drupal': 'https://www.drupal.org/favicon.ico',
  'Magento': 'https://magento.com/sites/all/themes/magento/favicon.ico',
  'PrestaShop': 'https://www.prestashop.com/sites/all/themes/prestashop/favicon.ico',
  'TYPO3': 'https://typo3.org/apple-touch-icon.png',
  'Ghost': 'https://ghost.org/favicon.ico',
  'Blogger': 'https://www.blogger.com/favicon.ico',
  'Hubspot': 'https://www.hubspot.com/favicon.ico',
};

export class WhatCmsService {
  private apiKey: string = ''; // La clé sera fournie via un paramètre pour éviter de l'encoder en dur
  private apiUrl: string = 'https://whatcms.org/API/Tech'; // Sera utilisé quand l'API réelle sera réactivée

  /**
   * Configure l'API key pour le service WhatCMS
   */
  public setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Détecte le CMS et autres technologies d'un site web via l'API WhatCMS.org
   */
  public async detectCms(url: string, apiKey?: string): Promise<DetectionResult> {
    // Utiliser la clé API fournie, celle stockée dans l'instance, ou récupérer depuis la base de données
    let key = apiKey || this.apiKey;
    
    // Si aucune clé n'est fournie, essayer de la récupérer depuis la base de données
    if (!key) {
      const dbKey = await apiKeyService.getApiKey('whatcms');
      if (dbKey) {
        key = dbKey;
        this.apiKey = dbKey; // Stocker pour les prochains appels
      }
    }
    
    if (!key) {
      throw new Error('WhatCMS API key is required. Please set it using setApiKey() or provide it as a parameter.');
    }

    // Vérifier et formater l'URL
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    // Extraire le domaine de l'URL (WhatCMS préfère les domaines sans protocole)
    const domain = url.replace(/^https?:\/\//, '').split('/')[0];

    try {
      // Appeler l'API WhatCMS.org
      const response = await axios.get<WhatCmsResponse>(this.apiUrl, {
        params: {
          key,
          url: domain
        }
      });

      // Vérifier si la requête a réussi
      if (response.data.result.code !== 200) {
        throw new Error(`WhatCMS API error: ${response.data.result.msg}`);
      }

      // Extraire les informations du CMS principal (le premier résultat, s'il existe)
      const mainCms = response.data.results.find(tech => 
        tech.categories.includes('CMS') || 
        tech.categories.includes('Blog') ||
        tech.name === 'WordPress' ||
        tech.name === 'Shopify' ||
        tech.name === 'Wix' ||
        tech.name === 'Webflow'
      );

      // Créer un résultat au format attendu par notre application
      const result: DetectionResult = {
        detected: response.data.results.length > 0,
        cms: mainCms ? mainCms.name : null,
        confidence: 90, // L'API WhatCMS est très fiable, on peut mettre une confiance élevée
        version: mainCms?.version,
        details: response.data,
        technologies: response.data.results,
        socialProfiles: response.data.meta?.social
      };

      // Ajouter le logo du CMS s'il est connu
      if (mainCms && cmsLogos[mainCms.name]) {
        result.logo = cmsLogos[mainCms.name];
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de la détection du CMS via WhatCMS:', error);
      
      // Retourner un résultat indiquant l'échec
      return {
        detected: false,
        cms: null,
        confidence: 0,
        details: {
          error: error instanceof Error ? error.message : 'Erreur inconnue',
          apiResponse: error instanceof axios.AxiosError ? error.response?.data : null
        }
      };
    }
  }

  /**
   * Génère des données fictives de détection CMS pour le développement
   * @param domain Domaine pour lequel simuler la détection CMS
   */
  generateMockCmsDetection(domain: string): DetectionResult {
    
    // Table de correspondance des CMS courants selon le domaine
    const domainToCms: Record<string, { name: string, version: string, confidence: number }> = {
      'wordpress': { name: 'WordPress', version: '6.3.2', confidence: 95 },
      'wix': { name: 'Wix', version: '2023', confidence: 92 },
      'shopify': { name: 'Shopify', version: '2023', confidence: 94 },
      'webflow': { name: 'Webflow', version: '2022', confidence: 90 },
      'joomla': { name: 'Joomla', version: '4.3.1', confidence: 85 },
      'drupal': { name: 'Drupal', version: '9.5.0', confidence: 88 },
      'magento': { name: 'Magento', version: '2.4.5', confidence: 92 },
      'squarespace': { name: 'Squarespace', version: '7.1', confidence: 89 },
      'prestashop': { name: 'PrestaShop', version: '8.0.3', confidence: 87 }
    };
    
    // Technologies communes trouvées sur les sites web
    const commonTechnologies: WhatCmsTechnology[] = [
      { name: 'Google Analytics', id: 1, categories: ['Analytics'], url: 'https://analytics.google.com' },
      { name: 'jQuery', id: 2, version: '3.6.3', categories: ['JavaScript Library'], url: 'https://jquery.com' },
      { name: 'Bootstrap', id: 3, version: '5.2.3', categories: ['UI Framework'], url: 'https://getbootstrap.com' },
      { name: 'Font Awesome', id: 4, categories: ['Font Script'], url: 'https://fontawesome.com' },
      { name: 'Google Fonts', id: 5, categories: ['Font Script'], url: 'https://fonts.google.com' },
      { name: 'Cloudflare', id: 6, categories: ['CDN'], url: 'https://cloudflare.com' }
    ];
    
    // Profils sociaux fictifs
    const socialProfiles: WhatCmsSocialProfile[] = [
      { network: 'Facebook', url: `https://facebook.com/${domain.split('.')[0]}`, profile: domain.split('.')[0] },
      { network: 'Twitter', url: `https://twitter.com/${domain.split('.')[0]}`, profile: '@' + domain.split('.')[0] },
      { network: 'LinkedIn', url: `https://linkedin.com/company/${domain.split('.')[0]}`, profile: domain.split('.')[0] }
    ];
    
    // Détecter le CMS basé sur le domaine
    let detectedCms = null;
    const domainLower = domain.toLowerCase();
    
    // Déterminer le CMS en cherchant des mots-clés dans le domaine
    for (const [keyword, cms] of Object.entries(domainToCms)) {
      if (domainLower.includes(keyword)) {
        detectedCms = cms;
        break;
      }
    }
    
    // Si aucun CMS n'est détecté par le nom de domaine, choisir un CMS aléatoirement
    if (!detectedCms) {
      const cmsKeys = Object.keys(domainToCms);
      const randomKey = cmsKeys[Math.floor(Math.random() * cmsKeys.length)];
      detectedCms = domainToCms[randomKey];
      detectedCms.confidence = Math.floor(Math.random() * 30) + 60; // Confiance plus faible pour une sélection aléatoire
    }
    
    // Sélectionner aléatoirement 2-4 technologies communes
    const selectedTechnologies = [...commonTechnologies];
    selectedTechnologies.sort(() => Math.random() - 0.5);
    const techCount = Math.floor(Math.random() * 3) + 2; // 2 à 4 technologies
    const technologies = selectedTechnologies.slice(0, techCount);
    
    // Ajouter le CMS détecté aux technologies
    if (detectedCms) {
      technologies.unshift({
        name: detectedCms.name,
        id: 1000,
        version: detectedCms.version,
        categories: ['CMS'],
        url: `https://${detectedCms.name.toLowerCase()}.com`
      });
    }
    
    // Logo du CMS
    const logoUrl = detectedCms ? cmsLogos[detectedCms.name] : undefined;
    
    return {
      detected: !!detectedCms,
      cms: detectedCms?.name || null,
      confidence: detectedCms?.confidence || 0,
      version: detectedCms?.version,
      details: { domain },
      logo: logoUrl,
      technologies,
      socialProfiles
    };
  }
}

// Exporter une instance du service
export const whatCmsService = new WhatCmsService();
